import { useEffect, useMemo, useState } from 'react'
import {
  loadSalesLedger,
  ledgerToCsv,
  downloadCsv,
  periodPreset,
  SALE_DATE_BASIS_LABEL,
  LedgerLine,
  SalesLedger,
} from '../../services/salesLedgerService'
import './SalespersonReport.css'

type SortKey = 'saleDate' | 'vehicle' | 'grossSale' | 'margin' | 'commission'

/*
 * Winnie, 1 Sep: "All report: should have two decimals instead of rounding amount."
 * Commission is a percentage of a margin, so it lands on cents far more often
 * than it lands on a round dollar. Rounding it for display made the report
 * disagree with what actually gets paid.
 */
const money = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const auDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

export default function SalespersonReport() {
  const [memberId, setMemberId] = useState<string>('')
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')
  const [realisedOnly, setRealisedOnly] = useState(true)
  const [basis, setBasis] = useState<'as_calculated' | 'realised'>('as_calculated')
  const [sortKey, setSortKey] = useState<SortKey>('saleDate')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const [data, setData] = useState<SalesLedger | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadSalesLedger({
      memberId: memberId || null,
      from: from || null,
      to: to || null,
      realisedOnly,
    })
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the report')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [memberId, from, to, realisedOnly, reloadKey])

  const applyPreset = (key: 'mtd' | 'qtd' | 'ytd' | 'last_month' | 'all') => {
    const p = periodPreset(key)
    setFrom(p.from || '')
    setTo(p.to || '')
  }

  const sorted = useMemo(() => {
    if (!data) return []
    const dir = sortDir === 'asc' ? 1 : -1
    const val = (l: LedgerLine) => {
      switch (sortKey) {
        case 'vehicle': return l.vehicle.toLowerCase()
        case 'grossSale': return basis === 'realised' ? l.grossSaleRealised ?? -1 : l.grossSaleEstimated
        case 'margin': return basis === 'realised' ? l.marginRealised ?? -Infinity : l.marginAsCalculated
        case 'commission': return basis === 'realised' ? l.commissionRealised ?? -Infinity : l.commissionAsCalculated
        default: return new Date(l.saleDate || 0).getTime()
      }
    }
    return [...data.lines].sort((a, b) => {
      const va = val(a)
      const vb = val(b)
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return 0
    })
  }, [data, sortKey, sortDir, basis])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(key)
      setSortDir(key === 'vehicle' ? 'asc' : 'desc')
    }
  }

  const memberName = data?.members.find((m) => m.id === memberId)?.name

  const handleExport = () => {
    if (!data) return
    const who = memberName ? memberName.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : 'all-salespeople'
    const period = from || to ? `${from || 'start'}_to_${to || 'today'}` : 'all-time'
    try {
      downloadCsv(`sales-performance_${who}_${period}.csv`, ledgerToCsv(sorted, data.totals))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed')
    }
  }

  const th = (key: SortKey, label: string, cls = '') => (
    <th className={`sortable ${cls}`} onClick={() => toggleSort(key)} title="Sort by this column">
      {label}
      <span className="sort-caret">{sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
    </th>
  )

  return (
    <div className="sp-report">
      <div className="sp-head">
        <div>
          <h3>Sales Performance by Salesperson</h3>
          <p className="sp-sub">
            One line per deal each person is credited on — VIN, vehicle, gross sale, margin and their commission.
          </p>
        </div>
        <button className="sp-btn primary" onClick={handleExport} disabled={!data || sorted.length === 0}>
          ⭳ Export CSV
        </button>
      </div>

      <div className="sp-filters">
        <label className="sp-field">
          <span>Salesperson</span>
          <select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
            <option value="">All Salespersons</option>
            {(data?.members || []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}{m.active === false ? ' (inactive)' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="sp-field">
          <span>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>

        <label className="sp-field">
          <span>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>

        <div className="sp-field">
          <span>Quick period</span>
          <div className="sp-presets">
            <button className="sp-chip" onClick={() => applyPreset('mtd')}>MTD</button>
            <button className="sp-chip" onClick={() => applyPreset('qtd')}>QTD</button>
            <button className="sp-chip" onClick={() => applyPreset('ytd')}>YTD</button>
            <button className="sp-chip" onClick={() => applyPreset('last_month')}>Last month</button>
            <button className="sp-chip" onClick={() => applyPreset('all')}>All Time</button>
          </div>
        </div>

        <label className="sp-field sp-check">
          <input type="checkbox" checked={realisedOnly} onChange={(e) => setRealisedOnly(e.target.checked)} />
          <span>Sold &amp; closed deals only</span>
        </label>

        <div className="sp-field">
          <span>Commission basis</span>
          <div className="sp-presets">
            <button
              className={`sp-chip ${basis === 'as_calculated' ? 'on' : ''}`}
              onClick={() => setBasis('as_calculated')}
              title="The figure the platform calculates today"
            >
              As calculated
            </button>
            <button
              className={`sp-chip ${basis === 'realised' ? 'on' : ''}`}
              onClick={() => setBasis('realised')}
              title="Invoiced sale less buy-in, capitalised costs and the buying fee ($500, or $250 where the deal makes a loss)"
            >
              Realised
            </button>
          </div>
        </div>
      </div>

      <p className="sp-basis-note">
        {basis === 'as_calculated' ? (
          <>
            <strong>As calculated</strong> is the projected figure: the attribution’s snapshotted rate ×
            the <em>net</em> margin — estimated sale less the purchase price, capitalised costs and the
            buying fee. A loss is shared at the same rate, as a deduction. Commission is quoted
            exclusive of GST here; the payee adds 10% on their invoice, and the GST-inclusive figure
            is on the Commission Reconciliation report.
          </>
        ) : (
          <>
            <strong>Realised</strong> uses the invoiced sale price less buy-in, capitalised transport and
            repairs, and the buying fee ($500 standard, $250 on a loss) — and carries losses through as a negative share. It is shown for
            comparison and is not what anyone is currently paid.
          </>
        )}
      </p>

      {error && (
        <div className="sp-error" role="alert">
          <span>{error}</span>
          <button className="sp-btn" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="sp-loading">Loading…</div>
      ) : !data ? null : (
        <>
          <div className="sp-stats">
            <Stat label="Deals" value={String(data.totals.deals)} />
            <Stat
              label={basis === 'realised' ? 'Gross sales (realised)' : 'Gross sales (estimated)'}
              value={money(basis === 'realised' ? data.totals.grossSaleRealised : data.totals.grossSaleEstimated)}
            />
            <Stat
              label={basis === 'realised' ? 'Margin (realised)' : 'Margin (as calculated)'}
              value={money(basis === 'realised' ? data.totals.marginRealised : data.totals.marginAsCalculated)}
            />
            <Stat
              label={basis === 'realised' ? 'Commission (realised)' : 'Commission (as calculated)'}
              value={money(basis === 'realised' ? data.totals.commissionRealised : data.totals.commissionAsCalculated)}
              accent
            />
          </div>

          {basis === 'realised' && data.totals.linesWithInvoice < sorted.length && (
            <p className="sp-warn">
              {sorted.length - data.totals.linesWithInvoice} of {sorted.length} lines have no invoice on file,
              so they contribute nothing to the realised totals. They are shown with “—”.
            </p>
          )}

          {data.unattributedCount > 0 && (
            <p className="sp-warn">
              {data.unattributedCount} deal{data.unattributedCount === 1 ? '' : 's'} in this period
              ({money(data.unattributedValue)}) have nobody credited on them, so they appear in no commission
              report. Add an attribution under <strong>Commissions → Deal Attributions</strong>.
            </p>
          )}

          {sorted.length === 0 ? (
            <div className="sp-empty">
              <p>
                No deals for {memberName || 'any salesperson'}
                {from || to ? ' in this period' : ''}.
              </p>
              <button
                className="sp-btn"
                onClick={() => {
                  setMemberId('')
                  setFrom('')
                  setTo('')
                  setRealisedOnly(false)
                }}
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    {th('saleDate', 'Sale date')}
                    <th>Deal</th>
                    <th>VIN</th>
                    {th('vehicle', 'Vehicle')}
                    {!memberId && <th>Salesperson</th>}
                    <th>Role / rate</th>
                    <th className="num">Buy-in</th>
                    {th('grossSale', 'Gross sale', 'num')}
                    {th('margin', 'Margin', 'num')}
                    {th('commission', 'Commission', 'num')}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((l, i) => {
                    const gross = basis === 'realised' ? l.grossSaleRealised : l.grossSaleEstimated
                    const margin = basis === 'realised' ? l.marginRealised : l.marginAsCalculated
                    const comm = basis === 'realised' ? l.commissionRealised : l.commissionAsCalculated
                    return (
                      <tr key={`${l.dealId}-${l.memberId}-${i}`}>
                        <td>
                          {auDate(l.saleDate)}
                          {l.saleDateBasis && l.saleDateBasis !== 'sold_at' && (
                            <span className="sp-basis-flag" title={`No sold date on file — using ${SALE_DATE_BASIS_LABEL[l.saleDateBasis].toLowerCase()}`}>
                              ~
                            </span>
                          )}
                        </td>
                        <td className="mono">{l.dealRef}</td>
                        <td className="mono">{l.vin || '—'}</td>
                        <td>{l.vehicle}</td>
                        {!memberId && <td>{l.memberName}</td>}
                        <td className="dim">{l.memberRole} · {l.termsLabel}</td>
                        <td className="num">{money(l.buyIn)}</td>
                        <td className="num">{money(gross)}</td>
                        <td className={`num ${margin !== null && margin < 0 ? 'neg' : ''}`}>{money(margin)}</td>
                        <td className={`num strong ${comm !== null && comm < 0 ? 'neg' : ''}`}>{money(comm)}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={memberId ? 6 : 7} className="total-label">
                      Total — {data.totals.deals} deal{data.totals.deals === 1 ? '' : 's'}
                      {memberName ? ` for ${memberName}` : ''}
                    </td>
                    <td className="num">
                      {money(basis === 'realised' ? data.totals.grossSaleRealised : data.totals.grossSaleEstimated)}
                    </td>
                    <td className="num">
                      {money(basis === 'realised' ? data.totals.marginRealised : data.totals.marginAsCalculated)}
                    </td>
                    <td className="num strong">
                      {money(basis === 'realised' ? data.totals.commissionRealised : data.totals.commissionAsCalculated)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p className="sp-footnote">
                A <strong>~</strong> next to a date means the deal has no sold date on file yet and the next
                best available date is being used.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`sp-stat${accent ? ' accent' : ''}`}>
      <div className="sp-stat-value">{value}</div>
      <div className="sp-stat-label">{label}</div>
    </div>
  )
}
